import type { SupportedLanguage } from "../services/language.service";
import { APP_NAME } from "./app.constants";

export interface PublicCopy {
  homeEyebrow: string;
  homeTitle: string;
  homeDescription: string;
  startChat: string;
  howItWorks: string;
  age: string;
  noSignup: string;
  worldwide: string;
  blogEyebrow: string;
  blogTitle: string;
  viewPosts: string;
  whyTitle: string;
  randomChat: string;
  randomChatText: string;
  interests: string;
  interestsText: string;
  privacy: string;
  privacyText: string;
  control: string;
  controlText: string;
  safetyEyebrow: string;
  safetyTitle: string;
  safetyText: string;
  safetyLink: string;
  seoTitle: string;
  faqTitle: string;
  blogRead: string;
  editorialTeam: string;
  footerExplore: string;
  footerLegal: string;
  footerReady: string;
  footerDescription: string;
  home: string;
  blog: string;
  about: string;
  pages: string;
  alternatives: string;
  explore: string;
  searchTitle: string;
  searchPlaceholder: string;
  noResults: string;
}

const english: PublicCopy = {
  homeEyebrow: "TEXT CHAT FOR THE CURIOUS",
  homeTitle: "Talk to Strangers Online - Free Anonymous Random Chat",
  homeDescription:
    "Make real connections through free anonymous random text chat. Meet people worldwide, find common interests, and start talking without signing up or building a public profile.",
  startChat: "Start chatting",
  howItWorks: "How it works",
  age: "18+ only",
  noSignup: "No signup required",
  worldwide: "Worldwide community",
  blogEyebrow: "FROM THE BLOG",
  blogTitle: "Tips and guides for chatting online",
  viewPosts: "View all posts",
  whyTitle: "A calmer way to meet someone new.",
  randomChat: "Random chat",
  randomChatText:
    "Find someone new in seconds, then move on with one tap when the conversation is done.",
  interests: "Start with common ground",
  interestsText:
    "Pick a few things you love and we will prioritize people who share them.",
  privacy: "Keep your privacy",
  privacyText:
    "No public profiles or endless setup. Start with a nickname and keep the rest private.",
  control: "Stay in control",
  controlText: "Talk, skip, and meet someone new whenever you are ready.",
  safetyEyebrow: "BUILT FOR SAFER CONVERSATIONS",
  safetyTitle: "Talk freely. Know you are in control.",
  safetyText:
    "Block, report, leave instantly, and keep your personal details to yourself.",
  safetyLink: "See safety tools",
  seoTitle: "Chat with strangers online",
  faqTitle: "Frequently asked questions",
  blogRead: "Read article",
  editorialTeam: `${APP_NAME} editorial team`,
  footerExplore: "Explore",
  footerLegal: "Legal",
  footerReady: "Ready for a new conversation?",
  footerDescription:
    "Anonymous text chat for curious people, built around privacy and real conversations.",
  home: "Home",
  blog: "Blog",
  about: "About",
  pages: "Pages",
  alternatives: "Alternatives",
  explore: "Explore",
  searchTitle: `Search ${APP_NAME}`,
  searchPlaceholder: "Search pages and guides...",
  noResults: "No matching pages yet.",
};

export const PUBLIC_COPY: Record<SupportedLanguage, PublicCopy> = {
  en: english,
  es: {
    ...english,
    homeEyebrow: "CHAT DE TEXTO PARA PERSONAS CURIOSAS",
    homeTitle: "Habla con desconocidos - Chat aleatorio anónimo y gratuito",
    homeDescription:
      "Crea conexiones reales con chat de texto aleatorio y anónimo. Conoce personas de todo el mundo y encuentra intereses comunes sin registrarte.",
    startChat: "Iniciar chat",
    howItWorks: "Cómo funciona",
    age: "Solo mayores de 18",
    noSignup: "Sin registro",
    worldwide: "Comunidad mundial",
    blogEyebrow: "DEL BLOG",
    blogTitle: "Consejos y guías para chatear en línea",
    viewPosts: "Ver todos los artículos",
    whyTitle: "Una forma más tranquila de conocer a alguien.",
    randomChat: "Chat aleatorio",
    interests: "Encuentra intereses comunes",
    privacy: "Protege tu privacidad",
    control: "Tú tienes el control",
    safetyEyebrow: "CONVERSACIONES MÁS SEGURAS",
    safetyTitle: "Habla libremente. Tú decides.",
    safetyText: "Bloquea, denuncia o sal en cualquier momento.",
    safetyLink: "Ver herramientas de seguridad",
    seoTitle: "Chatea con desconocidos en línea",
    faqTitle: "Preguntas frecuentes",
    blogRead: "Leer artículo",
    footerExplore: "Explorar",
    footerLegal: "Legal",
    footerReady: "¿Listo para una nueva conversación?",
    home: "Inicio",
    about: "Acerca de",
    pages: "Páginas",
    alternatives: "Alternativas",
    explore: "Explorar",
    searchTitle: `Buscar en ${APP_NAME}`,
    searchPlaceholder: "Buscar páginas y guías...",
    noResults: "No se encontraron páginas.",
  },
  de: {
    ...english,
    homeEyebrow: "TEXT-CHAT FÜR NEUGIERIGE",
    homeTitle: "Mit Fremden chatten - Kostenloser anonymer Zufalls-Chat",
    homeDescription:
      "Knüpfe echte Kontakte mit kostenlosem anonymem Text-Chat. Triff Menschen weltweit und finde gemeinsame Interessen ohne Anmeldung.",
    startChat: "Chat starten",
    howItWorks: "So funktioniert es",
    age: "Nur ab 18",
    noSignup: "Keine Anmeldung",
    worldwide: "Weltweite Community",
    blogEyebrow: "AUS DEM BLOG",
    blogTitle: "Tipps und Ratgeber für Online-Chats",
    viewPosts: "Alle Beiträge ansehen",
    whyTitle: "Eine ruhigere Art, jemanden kennenzulernen.",
    randomChat: "Zufälliger Chat",
    interests: "Gemeinsame Interessen",
    privacy: "Deine Privatsphäre",
    control: "Du bestimmst",
    safetyEyebrow: "SICHERERE GESPRÄCHE",
    safetyTitle: "Sprich frei. Du hast die Kontrolle.",
    safetyText: "Blockieren, melden oder jederzeit gehen.",
    safetyLink: "Sicherheitstools ansehen",
    seoTitle: "Mit Fremden online chatten",
    faqTitle: "Häufige Fragen",
    blogRead: "Artikel lesen",
    footerExplore: "Entdecken",
    footerLegal: "Rechtliches",
    footerReady: "Bereit für ein neues Gespräch?",
    home: "Startseite",
    about: "Über uns",
    pages: "Seiten",
    alternatives: "Alternativen",
    explore: "Entdecken",
    searchTitle: `${APP_NAME} durchsuchen`,
    searchPlaceholder: "Seiten und Ratgeber suchen...",
    noResults: "Keine passenden Seiten gefunden.",
  },
  fr: {
    ...english,
    homeEyebrow: "CHAT TEXTE POUR LES CURIEUX",
    homeTitle: "Parlez à des inconnus - Chat aléatoire anonyme et gratuit",
    homeDescription:
      "Créez de vraies connexions grâce au chat texte anonyme. Rencontrez des personnes du monde entier sans inscription.",
    startChat: "Démarrer le chat",
    howItWorks: "Comment ça marche",
    age: "18 ans et plus",
    noSignup: "Sans inscription",
    worldwide: "Communauté mondiale",
    blogEyebrow: "DU BLOG",
    blogTitle: "Conseils et guides pour discuter en ligne",
    viewPosts: "Voir tous les articles",
    whyTitle: "Une façon plus douce de rencontrer quelqu'un.",
    randomChat: "Chat aléatoire",
    interests: "Trouver des intérêts communs",
    privacy: "Protéger votre vie privée",
    control: "Vous gardez le contrôle",
    safetyEyebrow: "DES CONVERSATIONS PLUS SÛRES",
    safetyTitle: "Parlez librement. Vous décidez.",
    safetyText: "Bloquez, signalez ou partez à tout moment.",
    safetyLink: "Voir les outils de sécurité",
    seoTitle: "Parler à des inconnus en ligne",
    faqTitle: "Questions fréquentes",
    blogRead: "Lire l'article",
    footerExplore: "Explorer",
    footerLegal: "Informations légales",
    footerReady: "Prêt pour une nouvelle conversation ?",
    home: "Accueil",
    about: "À propos",
    pages: "Pages",
    alternatives: "Alternatives",
    explore: "Explorer",
    searchTitle: `Rechercher ${APP_NAME}`,
    searchPlaceholder: "Rechercher des pages et guides...",
    noResults: "Aucune page trouvée.",
  },
  it: {
    ...english,
    homeEyebrow: "CHAT TESTUALE PER CURIOSI",
    homeTitle: "Parla con sconosciuti - Chat casuale anonima e gratuita",
    homeDescription:
      "Crea connessioni reali con una chat testuale anonima. Incontra persone da tutto il mondo senza registrarti.",
    startChat: "Inizia a chattare",
    howItWorks: "Come funziona",
    age: "Solo maggiorenni",
    noSignup: "Nessuna registrazione",
    worldwide: "Comunità mondiale",
    blogEyebrow: "DAL BLOG",
    blogTitle: "Consigli e guide per chattare online",
    viewPosts: "Vedi tutti gli articoli",
    whyTitle: "Un modo più sereno per conoscere qualcuno.",
    randomChat: "Chat casuale",
    interests: "Trovate interessi comuni",
    privacy: "Proteggi la tua privacy",
    control: "Sei tu a decidere",
    safetyEyebrow: "CONVERSAZIONI PIÙ SICURE",
    safetyTitle: "Parla liberamente. Hai il controllo.",
    safetyText: "Blocca, segnala o esci in qualsiasi momento.",
    safetyLink: "Vedi gli strumenti di sicurezza",
    seoTitle: "Chattare con sconosciuti online",
    faqTitle: "Domande frequenti",
    blogRead: "Leggi l'articolo",
    footerExplore: "Esplora",
    footerLegal: "Legale",
    footerReady: "Pronto per una nuova conversazione?",
    home: "Home",
    about: "Chi siamo",
    pages: "Pagine",
    alternatives: "Alternative",
    explore: "Esplora",
    searchTitle: `Cerca ${APP_NAME}`,
    searchPlaceholder: "Cerca pagine e guide...",
    noResults: "Nessuna pagina trovata.",
  },
  "pt-br": {
    ...english,
    homeEyebrow: "CHAT DE TEXTO PARA CURIOSOS",
    homeTitle: "Converse com desconhecidos - Chat aleatório anônimo e grátis",
    homeDescription:
      "Crie conexões reais com chat de texto anônimo. Conheça pessoas do mundo todo sem fazer cadastro.",
    startChat: "Começar a conversar",
    howItWorks: "Como funciona",
    age: "Apenas maiores de 18",
    noSignup: "Sem cadastro",
    worldwide: "Comunidade mundial",
    blogEyebrow: "DO BLOG",
    blogTitle: "Dicas e guias para conversar online",
    viewPosts: "Ver todos os artigos",
    whyTitle: "Uma forma mais tranquila de conhecer alguém.",
    randomChat: "Chat aleatório",
    interests: "Encontre interesses em comum",
    privacy: "Proteja sua privacidade",
    control: "Você está no controle",
    safetyEyebrow: "CONVERSAS MAIS SEGURAS",
    safetyTitle: "Fale livremente. Você decide.",
    safetyText: "Bloqueie, denuncie ou saia quando quiser.",
    safetyLink: "Ver ferramentas de segurança",
    seoTitle: "Converse com desconhecidos online",
    faqTitle: "Perguntas frequentes",
    blogRead: "Ler artigo",
    footerExplore: "Explorar",
    footerLegal: "Legal",
    footerReady: "Pronto para uma nova conversa?",
    home: "Início",
    about: "Sobre",
    pages: "Páginas",
    alternatives: "Alternativas",
    explore: "Explorar",
    searchTitle: `Pesquisar ${APP_NAME}`,
    searchPlaceholder: "Pesquisar páginas e guias...",
    noResults: "Nenhuma página encontrada.",
  },
  ja: {
    ...english,
    homeEyebrow: "好奇心から始まるテキストチャット",
    homeTitle: "知らない人と話そう - 無料で匿名のランダムチャット",
    homeDescription:
      "匿名のテキストチャットで世界中の人と出会えます。登録せずに共通の興味から会話を始めましょう。",
    startChat: "チャットを始める",
    howItWorks: "使い方",
    age: "18歳以上",
    noSignup: "登録不要",
    worldwide: "世界中のコミュニティ",
    blogEyebrow: "ブログから",
    blogTitle: "オンラインチャットのヒントとガイド",
    viewPosts: "記事をすべて見る",
    whyTitle: "新しい人と出会う、より穏やかな方法。",
    randomChat: "ランダムチャット",
    interests: "共通の興味から始める",
    privacy: "プライバシーを守る",
    control: "自分のペースで",
    safetyEyebrow: "より安全な会話のために",
    safetyTitle: "自由に話そう。主導権はあなたに。",
    safetyText: "ブロック、報告、退出をいつでも行えます。",
    safetyLink: "安全機能を見る",
    seoTitle: "オンラインで知らない人とチャット",
    faqTitle: "よくある質問",
    blogRead: "記事を読む",
    footerExplore: "探す",
    footerLegal: "規約",
    footerReady: "新しい会話を始めませんか？",
    home: "ホーム",
    about: "概要",
    pages: "ページ",
    alternatives: "代替サービス",
    explore: "探す",
    searchTitle: `${APP_NAME}を検索`,
    searchPlaceholder: "ページとガイドを検索...",
    noResults: "一致するページがありません。",
  },
};
